import { find } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { shopsApi } from 'api';
import {
  AdditionalOrderColumn,
  CallbackUrlStatus,
  FilterDefinitionType,
  OrdersTab,
  PayoutDisputeStatusDetails,
  QueryKey,
} from 'enums';
import { Orders } from 'features/payout-orders';
import {
  useCurrencies,
  useOrderQueryFilters,
  useShopsQuery,
  useUserContext,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import { FilterDefinition, OrderFilters } from 'types';
import { formUtils, orderUtils, requisitesUtils } from 'utils';

export const MerchantDisputeOrders: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders',
  });
  const { paymentTypes, banks } = useUserContext();
  const { getFiatCurrencyCode, fiatCurrenciesOptions } = useCurrencies();

  const queryResultShops = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllAsMerchant,
  );

  useOrderQueryFilters();

  const additionalFilters: FilterDefinition<OrderFilters>[] = useMemo(
    () => [
      {
        label: t('filters.customer'),
        name: 'customerId',
        type: FilterDefinitionType.Text,
      },
      {
        label: t('filters.shop'),
        name: 'shopId',
        type: FilterDefinitionType.Shop,
        shops: queryResultShops.data,
      },
      {
        label: t('resolve.title_dispute'),
        name: 'statusDetails',
        type: FilterDefinitionType.Enum,
        enum: Object.values(PayoutDisputeStatusDetails),
        getDisplayName: (value: PayoutDisputeStatusDetails) =>
          orderUtils.getPayoutStatusDetailsLabel(value),
      },
      {
        label: t('filters.external_order_id'),
        name: 'externalOrderId',
        type: FilterDefinitionType.Text,
      },
      {
        label: t('filters.callback_url_status'),
        name: 'callbackUrlStatus',
        type: FilterDefinitionType.Enum,
        enum: CallbackUrlStatus,
        getDisplayName: orderUtils.getCallbackUrlStatusLabel,
      },
      {
        label: t('filters.payment_type'),
        name: 'paymentTypeId',
        type: FilterDefinitionType.Select,
        options: requisitesUtils.getPaymentTypesOptions(paymentTypes),
        getDisplayName: (value: string) =>
          requisitesUtils.getPaymentTypeLabel(
            find(paymentTypes, { id: value })!,
          ),
      },
      {
        label: t('filters.bank'),
        name: 'bankId',
        type: FilterDefinitionType.Select,
        options: formUtils.getOptions(banks),
        getDisplayName: (value: string) => find(banks, { id: value })?.name,
      },
      {
        label: t('filters.fiat_currency'),
        name: 'fiatCurrencyId',
        type: FilterDefinitionType.Select,
        options: fiatCurrenciesOptions,
        getDisplayName: getFiatCurrencyCode,
      },
    ],
    [
      banks,
      fiatCurrenciesOptions,
      getFiatCurrencyCode,
      paymentTypes,
      queryResultShops.data,
      t,
    ],
  );

  return (
    <Orders
      tab={OrdersTab.Dispute}
      additionalFilters={additionalFilters}
      additionalColumns={[
        AdditionalOrderColumn.CustomerContacts,
        AdditionalOrderColumn.Shop,
        AdditionalOrderColumn.CustomerPayment,
        AdditionalOrderColumn.Dispute,
      ]}
    />
  );
};
